import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dsWikiService } from '../services';
import type { Curator as ApiCurator } from '../services/types';

type TabType = 'pending' | 'approved' | 'rejected' | 'requests_to_me' | 'my_requests';
type ActionType = 'approve' | 'reject' | 'request_review';
type SuggestionType = 'correction' | 'addition' | 'removal' | 'rewrite';

interface ReviewItem {
  id: number;
  title: string;
  summary: string;
  author: string;
  submittedDate: string;
  department: string;
  status: 'pending' | 'approved' | 'rejected' | 'changes_requested';
  priority: 'high' | 'medium' | 'low';
  reviewedBy?: string;
  reviewedDate?: string;
  comments?: string;
  requestedFrom?: string;
  requestedTo?: string;
  requestMessage?: string;
  changesCount?: number;
  content?: ArticleContent;
}

interface ArticleContent {
  sections: ContentSection[];
}

interface ContentSection {
  id: string;
  type: 'title' | 'heading' | 'paragraph' | 'image' | 'table' | 'list';
  content: string;
  imageUrl?: string;
  tableData?: string[][];
  listItems?: string[];
}

interface Suggestion {
  id: string;
  sectionId: string;
  sectionType: string;
  originalContent: string;
  suggestionType: SuggestionType;
  suggestionText: string;
  createdAt: Date;
}

interface Curator {
  id: string;
  name: string;
  avatar: string;
  department: string;
  reviewsCompleted: number;
  online: boolean;
}

export default function CuratorInbox() {
  const [activeTab, setActiveTab] = useState<TabType>('pending');
  const [selectedItem, setSelectedItem] = useState<ReviewItem | null>(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState<ActionType | null>(null);
  const [actionComment, setActionComment] = useState('');
  const [selectedCurator, setSelectedCurator] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  // Suggestion states
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [selectedSection, setSelectedSection] = useState<ContentSection | null>(null);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [suggestionType, setSuggestionType] = useState<SuggestionType>('correction');
  const [suggestionText, setSuggestionText] = useState('');

  // API data states
  const [apiCurators, setApiCurators] = useState<ApiCurator[]>([]);
  const [apiLoading, setApiLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);

  // Fetch curators from API on mount
  useEffect(() => {
    const fetchCurators = async () => {
      setApiLoading(true);
      setApiError(null);
      try {
        const response = await dsWikiService.getActiveCurators();
        setApiCurators(response.data || []);
      } catch (err: any) {
        console.error('Error fetching curators:', err);
        setApiError(err.message || 'Failed to fetch curators');
      } finally {
        setApiLoading(false);
      }
    };

    fetchCurators();
  }, []);

  // Transform API curators to UI format
  const curators: Curator[] = apiCurators.map(c => ({
    id: String(c.dswiki_crtr_id),
    name: c.crtr_nm || `Curator ${c.dswiki_crtr_id}`,
    avatar: (c.crtr_nm || 'C').charAt(0).toUpperCase(),
    department: c.dpt_nm || 'General',
    reviewsCompleted: (c.rvw_cmplt_ct || 0) + (c.aprv_ct || 0),
    online: c.a_in || false
  }));

  // Current curator - will be populated from authenticated user session
  // For now, use first curator from API or a placeholder
  const currentCurator = apiCurators.length > 0 ? {
    name: apiCurators[0].crtr_nm || 'Curator',
    role: 'Curator',
    department: apiCurators[0].dpt_nm || 'General'
  } : {
    name: 'Loading...',
    role: 'Curator',
    department: ''
  };

  // Stats - will be populated from API when review items API is available
  // For now, show zeros
  const stats = {
    pending: 0,
    approved: 0,
    rejected: 0,
    requestsToMe: 0,
    myRequests: 0
  };

  // Review items data - will be populated from API when available
  // TODO: Call review items API when available
  const reviewItems: ReviewItem[] = [];
  const sampleContent: ArticleContent = { sections: [] };

  // Filter items
  const filteredItems = useMemo(() => {
    let items = reviewItems;
    if (activeTab === 'pending') {
      items = items.filter(item => item.status === 'pending' && !item.requestedFrom && !item.requestedTo);
    } else if (activeTab === 'approved') {
      items = items.filter(item => item.status === 'approved');
    } else if (activeTab === 'rejected') {
      items = items.filter(item => item.status === 'rejected');
    } else if (activeTab === 'requests_to_me') {
      items = items.filter(item => item.requestedFrom);
    } else if (activeTab === 'my_requests') {
      items = items.filter(item => item.requestedTo);
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item =>
        item.title.toLowerCase().includes(query) ||
        item.author.toLowerCase().includes(query)
      );
    }
    return items;
  }, [activeTab, searchQuery]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'border-l-red-500';
      case 'medium': return 'border-l-yellow-500';
      case 'low': return 'border-l-gray-300';
      default: return 'border-l-gray-300';
    }
  };

  const handleSelectArticle = (item: ReviewItem) => {
    setSelectedItem(item);
    setSuggestions([]);
  };

  const handleSectionClick = (section: ContentSection) => {
    if (activeTab === 'pending' || activeTab === 'requests_to_me') {
      setSelectedSection(section);
      setShowSuggestionModal(true);
      setSuggestionType('correction');
      setSuggestionText('');
    }
  };

  const addSuggestion = () => {
    if (!selectedSection || !suggestionText.trim()) return;

    const newSuggestion: Suggestion = {
      id: `sug-${Date.now()}`,
      sectionId: selectedSection.id,
      sectionType: selectedSection.type,
      originalContent: selectedSection.type === 'list'
        ? selectedSection.listItems?.join(', ') || ''
        : selectedSection.type === 'table'
        ? 'Table content'
        : selectedSection.content,
      suggestionType,
      suggestionText,
      createdAt: new Date()
    };

    setSuggestions([...suggestions, newSuggestion]);
    setShowSuggestionModal(false);
    setSelectedSection(null);
    setSuggestionText('');
  };

  const removeSuggestion = (id: string) => {
    setSuggestions(suggestions.filter(s => s.id !== id));
  };

  const submitAllSuggestions = () => {
    if (suggestions.length === 0) return;
    console.log('Submitting suggestions:', suggestions);
    alert(`${suggestions.length} suggestion(s) submitted successfully for "${selectedItem?.title}"`);
    setSuggestions([]);
  };

  const handleAction = (action: ActionType) => {
    setActionType(action);
    setActionComment('');
    setSelectedCurator('');
    setShowActionModal(true);
  };

  const submitAction = () => {
    if (!selectedItem || !actionType) return;
    console.log('Action:', actionType, 'Item:', selectedItem.id);
    alert(`${actionType.replace('_', ' ').toUpperCase()} completed for "${selectedItem.title}"`);
    setShowActionModal(false);
    setActionType(null);
    setActionComment('');
    setSelectedCurator('');
  };

  const getSuggestionTypeColor = (type: SuggestionType) => {
    switch (type) {
      case 'correction': return 'bg-blue-100 text-blue-700';
      case 'addition': return 'bg-green-100 text-green-700';
      case 'removal': return 'bg-red-100 text-red-700';
      case 'rewrite': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const hasSuggestion = (sectionId: string) => {
    return suggestions.some(s => s.sectionId === sectionId);
  };

  const renderSection = (section: ContentSection) => {
    const isSelectable = activeTab === 'pending' || activeTab === 'requests_to_me';
    const hasExistingSuggestion = hasSuggestion(section.id);
    const baseClass = `relative group ${isSelectable ? 'cursor-pointer hover:bg-yellow-50 transition-colors' : ''} ${hasExistingSuggestion ? 'bg-orange-50 border-l-4 border-orange-400 pl-3' : ''}`;

    const content = section.type === 'title' ? selectedItem?.title : section.content;

    switch (section.type) {
      case 'title':
        return (
          <div key={section.id} className={baseClass} onClick={() => handleSectionClick({ ...section, content: selectedItem?.title || '' })}>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">{selectedItem?.title}</h1>
            {isSelectable && <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-xs text-indigo-600"><i className="ri-add-circle-line mr-1"></i>Add suggestion</div>}
          </div>
        );
      case 'heading':
        return (
          <div key={section.id} className={`${baseClass} mt-6`} onClick={() => handleSectionClick(section)}>
            <h2 className="text-lg font-semibold text-gray-800">{content}</h2>
            {isSelectable && <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-xs text-indigo-600"><i className="ri-add-circle-line mr-1"></i>Add suggestion</div>}
          </div>
        );
      case 'paragraph':
        return (
          <div key={section.id} className={`${baseClass} my-3`} onClick={() => handleSectionClick(section)}>
            <p className="text-gray-600 leading-relaxed">{content}</p>
            {isSelectable && <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 text-xs text-indigo-600"><i className="ri-add-circle-line mr-1"></i>Add suggestion</div>}
          </div>
        );
      case 'image':
        return (
          <div key={section.id} className={`${baseClass} my-4`} onClick={() => handleSectionClick(section)}>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <img src={section.imageUrl} alt={content} className="w-full h-48 object-cover" />
              <p className="text-sm text-gray-500 text-center py-2 bg-gray-50">{content}</p>
            </div>
            {isSelectable && <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 text-xs text-indigo-600 bg-white px-2 py-1 rounded shadow"><i className="ri-add-circle-line mr-1"></i>Add suggestion</div>}
          </div>
        );
      case 'list':
        return (
          <div key={section.id} className={`${baseClass} my-3`} onClick={() => handleSectionClick(section)}>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              {section.listItems?.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
            {isSelectable && <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 text-xs text-indigo-600"><i className="ri-add-circle-line mr-1"></i>Add suggestion</div>}
          </div>
        );
      case 'table':
        return (
          <div key={section.id} className={`${baseClass} my-4`} onClick={() => handleSectionClick(section)}>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {section.tableData?.[0]?.map((cell, idx) => (
                      <th key={idx} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{cell}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {section.tableData?.slice(1).map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      {row.map((cell, cellIdx) => (
                        <td key={cellIdx} className="px-4 py-2 text-sm text-gray-600">{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {isSelectable && <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 text-xs text-indigo-600 bg-white px-2 py-1 rounded shadow"><i className="ri-add-circle-line mr-1"></i>Add suggestion</div>}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/app/ds-wiki" className="text-gray-500 hover:text-gray-700">
                <i className="ri-arrow-left-line text-xl"></i>
              </Link>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center">
                  <i className="ri-inbox-2-line mr-2 text-indigo-600"></i>
                  Curator Inbox
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">{currentCurator.name}</span>
              <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white text-sm font-semibold">
                {currentCurator.name.charAt(0)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-60px)]">
        {/* Left Panel - Article List */}
        <div className="w-80 border-r border-gray-200 bg-white flex flex-col">
          {/* Tabs */}
          <div className="p-3 border-b border-gray-200">
            <div className="flex flex-wrap gap-1">
              {[
                { key: 'pending', label: 'Pending', count: stats.pending, color: 'amber' },
                { key: 'approved', label: 'Approved', count: stats.approved, color: 'green' },
                { key: 'rejected', label: 'Rejected', count: stats.rejected, color: 'red' },
                { key: 'requests_to_me', label: 'To Me', count: stats.requestsToMe, color: 'blue' },
                { key: 'my_requests', label: 'My Req', count: stats.myRequests, color: 'indigo' },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => { setActiveTab(tab.key as TabType); setSelectedItem(null); setSuggestions([]); }}
                  className={`px-2 py-1 rounded text-xs font-medium transition-all flex items-center gap-1 ${
                    activeTab === tab.key
                      ? `bg-${tab.color}-100 text-${tab.color}-700`
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  {tab.label}
                  <span className={`px-1 rounded text-xs ${activeTab === tab.key ? `bg-${tab.color}-200` : 'bg-gray-200'}`}>{tab.count}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Search */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <i className="ri-search-line absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles..."
                className="w-full pl-7 pr-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Article List */}
          <div className="flex-1 overflow-y-auto">
            {filteredItems.length > 0 ? (
              filteredItems.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleSelectArticle(item)}
                  className={`p-3 border-b border-gray-100 cursor-pointer transition-colors border-l-4 ${getPriorityColor(item.priority)} ${
                    selectedItem?.id === item.id ? 'bg-indigo-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <h3 className={`text-sm font-medium mb-1 line-clamp-2 ${selectedItem?.id === item.id ? 'text-indigo-700' : 'text-gray-900'}`}>
                    {item.title}
                  </h3>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{item.author}</span>
                    <span>{new Date(item.submittedDate).toLocaleDateString()}</span>
                  </div>
                  {item.requestedFrom && (
                    <div className="mt-1 text-xs text-blue-600">
                      <i className="ri-user-received-2-line mr-1"></i>From: {item.requestedFrom}
                    </div>
                  )}
                  {item.requestedTo && (
                    <div className="mt-1 text-xs text-indigo-600">
                      <i className="ri-user-follow-line mr-1"></i>To: {item.requestedTo}
                    </div>
                  )}
                  {item.changesCount && item.changesCount > 0 && (
                    <div className="mt-1 text-xs text-orange-600">
                      <i className="ri-edit-line mr-1"></i>{item.changesCount} suggestion(s)
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="p-6 text-center text-gray-500 text-sm">
                <i className="ri-inbox-line text-3xl mb-2 block text-gray-300"></i>
                No articles found
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Article Preview */}
        <div className="flex-1 flex flex-col bg-gray-50">
          {selectedItem ? (
            <>
              {/* Article Header */}
              <div className="bg-white border-b border-gray-200 p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        selectedItem.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        selectedItem.status === 'approved' ? 'bg-green-100 text-green-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {selectedItem.status.charAt(0).toUpperCase() + selectedItem.status.slice(1)}
                      </span>
                      <span className="text-xs text-gray-500">{selectedItem.department}</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      By <span className="font-medium">{selectedItem.author}</span> on {new Date(selectedItem.submittedDate).toLocaleDateString()}
                    </p>
                  </div>
                  {(activeTab === 'pending' || activeTab === 'requests_to_me') && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleAction('approve')}
                        className="px-3 py-1.5 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700 flex items-center gap-1"
                      >
                        <i className="ri-check-line"></i>Approve
                      </button>
                      <button
                        onClick={() => handleAction('reject')}
                        className="px-3 py-1.5 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700 flex items-center gap-1"
                      >
                        <i className="ri-close-line"></i>Reject
                      </button>
                      {activeTab === 'pending' && (
                        <button
                          onClick={() => handleAction('request_review')}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 flex items-center gap-1"
                        >
                          <i className="ri-user-follow-line"></i>Ask Review
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {(activeTab === 'pending' || activeTab === 'requests_to_me') && (
                  <p className="text-xs text-indigo-600 mt-2">
                    <i className="ri-lightbulb-line mr-1"></i>
                    Click on any section (title, paragraph, image, table) to add a suggestion
                  </p>
                )}
              </div>

              {/* Article Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm p-8">
                  {selectedItem.content?.sections.map(section => renderSection(section))}
                </div>
              </div>

              {/* Suggestions Panel */}
              {suggestions.length > 0 && (
                <div className="bg-white border-t border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900 flex items-center">
                      <i className="ri-chat-quote-line mr-2 text-orange-500"></i>
                      Your Suggestions ({suggestions.length})
                    </h3>
                    <button
                      onClick={submitAllSuggestions}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 flex items-center gap-2"
                    >
                      <i className="ri-send-plane-line"></i>
                      Submit All Suggestions
                    </button>
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {suggestions.map((sug) => (
                      <div key={sug.id} className="flex-shrink-0 w-64 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getSuggestionTypeColor(sug.suggestionType)}`}>
                            {sug.suggestionType}
                          </span>
                          <button onClick={() => removeSuggestion(sug.id)} className="text-gray-400 hover:text-red-500">
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mb-1 truncate">On: {sug.sectionType}</p>
                        <p className="text-sm text-gray-700 line-clamp-2">{sug.suggestionText}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <i className="ri-file-text-line text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-lg font-medium text-gray-700 mb-2">Select an article to review</h3>
                <p className="text-sm">Choose an article from the list to view its content</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Suggestion Modal */}
      {showSuggestionModal && selectedSection && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowSuggestionModal(false)}>
          <div className="bg-white rounded-xl w-full max-w-lg mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b bg-orange-50">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <i className="ri-edit-line mr-2 text-orange-600"></i>
                  Add Suggestion
                </h3>
                <button onClick={() => setShowSuggestionModal(false)} className="text-gray-400 hover:text-gray-600">
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Selected {selectedSection.type}:</p>
                <p className="text-sm text-gray-700 line-clamp-3">
                  {selectedSection.type === 'list'
                    ? selectedSection.listItems?.join(', ')
                    : selectedSection.type === 'table'
                    ? 'Table content'
                    : selectedSection.type === 'image'
                    ? selectedSection.content
                    : selectedSection.content}
                </p>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Suggestion Type</label>
                <div className="flex gap-2">
                  {(['correction', 'addition', 'removal', 'rewrite'] as SuggestionType[]).map((type) => (
                    <button
                      key={type}
                      onClick={() => setSuggestionType(type)}
                      className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                        suggestionType === type ? getSuggestionTypeColor(type) : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Suggestion</label>
                <textarea
                  value={suggestionText}
                  onChange={(e) => setSuggestionText(e.target.value)}
                  placeholder="Describe your suggestion for this content..."
                  rows={4}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-end gap-3">
              <button onClick={() => setShowSuggestionModal(false)} className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium text-sm">
                Cancel
              </button>
              <button
                onClick={addSuggestion}
                disabled={!suggestionText.trim()}
                className={`px-6 py-2 rounded-lg font-medium text-sm transition-all ${
                  !suggestionText.trim() ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-orange-600 text-white hover:bg-orange-700'
                }`}
              >
                Add Suggestion
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {showActionModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowActionModal(false)}>
          <div className="bg-white rounded-xl w-full max-w-lg mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className={`px-6 py-4 border-b ${
              actionType === 'approve' ? 'bg-green-50' :
              actionType === 'reject' ? 'bg-red-50' : 'bg-blue-50'
            }`}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <i className={`mr-2 ${
                    actionType === 'approve' ? 'ri-check-double-line text-green-600' :
                    actionType === 'reject' ? 'ri-close-circle-line text-red-600' :
                    'ri-user-follow-line text-blue-600'
                  }`}></i>
                  {actionType === 'approve' ? 'Approve Article' :
                   actionType === 'reject' ? 'Reject Article' : 'Request Review'}
                </h3>
                <button onClick={() => setShowActionModal(false)} className="text-gray-400 hover:text-gray-600">
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-900">{selectedItem.title}</p>
                <p className="text-xs text-gray-500 mt-1">By {selectedItem.author}</p>
              </div>
              {actionType === 'request_review' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Curator</label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {curators.map((curator) => (
                      <button
                        key={curator.id}
                        onClick={() => setSelectedCurator(curator.id)}
                        className={`w-full flex items-center gap-3 p-2 rounded-lg border transition-all ${
                          selectedCurator === curator.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold ${curator.online ? 'bg-indigo-500' : 'bg-gray-400'}`}>
                          {curator.avatar}
                        </div>
                        <div className="flex-1 text-left">
                          <p className="text-sm font-medium text-gray-900">{curator.name}</p>
                          <p className="text-xs text-gray-500">{curator.department}</p>
                        </div>
                        {selectedCurator === curator.id && <i className="ri-check-line text-blue-600"></i>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {actionType === 'approve' ? 'Notes (Optional)' : actionType === 'reject' ? 'Reason for Rejection' : 'Message'}
                </label>
                <textarea
                  value={actionComment}
                  onChange={(e) => setActionComment(e.target.value)}
                  placeholder={actionType === 'reject' ? 'Please explain why...' : 'Add a message...'}
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-end gap-3">
              <button onClick={() => setShowActionModal(false)} className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium text-sm">
                Cancel
              </button>
              <button
                onClick={submitAction}
                disabled={(actionType === 'reject' && !actionComment.trim()) || (actionType === 'request_review' && !selectedCurator)}
                className={`px-6 py-2 rounded-lg font-medium text-sm transition-all ${
                  (actionType === 'reject' && !actionComment.trim()) || (actionType === 'request_review' && !selectedCurator)
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : actionType === 'approve' ? 'bg-green-600 text-white hover:bg-green-700' :
                      actionType === 'reject' ? 'bg-red-600 text-white hover:bg-red-700' :
                      'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {actionType === 'approve' ? 'Approve' : actionType === 'reject' ? 'Reject' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
